import Skeleton from '@material-ui/lab/Skeleton';
import React, { JSX } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { ECurrencyType } from '~/src/dash/api/exchange-rates';
import { IDrillBarChartDataItem } from '~/src/dash/components/charts/common';
import { formatCellValue, styleCellValue, valueTypes } from '~/src/dash/components/tables/common';
import { convertLargeNumbersToRoundedString } from '~/src/dash/utils/basic';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  LoadingState,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { Col, Cols, Row, Rows, Span, TKey, Text, fontSizing, spacing } from '~/src/dash/publicaUI';
import { dateRangeStringToTKey } from '~/src/dash/views/brands-dashboard/config';

export const defaultColorBank = {
  blue: '#5D66FA',
  red: '#FF4E29',
  cyan: '#ACF8F4',
  mustard: '#F3C628',
  coralPink: '#FB6376',
  mahogany: '#A75E6E',
  blueGray: '#84BCEF',
  darkPeach: '#F4B583',
  forestGreen: '#40916C',
  seaGreen: '#85FFC7',
  ochre: '#E6DFA4',
  pink: '#FF9CEF',
  purple: '#6F60DD',
  yellow: '#FFFD82',
  gray: '#8E9AAF',
  orange: '#FFA737',
  turquoise: '#00B7A9',
  lavender: '#DAC3E8',
  limeGreen: '#B2E061',
  tan: '#C6AC8F',
  sienna: '#E88160',
  palePink: '#E8C2D2',
  darkBlue: '#014F86',
  purpleBlue: '#95619C',
  darkGray: '#8B8C89',
};

interface ICustomTooltipProps {
  active: boolean;
  payload: any;
  label: string;
  dateRange: string;
  isComparedToEnabled: boolean;
  eCurrencyType?: ECurrencyType;
  dateRangeTKey?: TKey;
}

const CustomTooltip = ({
  active,
  payload,
  dateRange,
  isComparedToEnabled = false,
  eCurrencyType,
  dateRangeTKey,
}: ICustomTooltipProps): JSX.Element | null => {
  if (active && payload && payload.length) {
    const item = payload[0].payload;
    const { name, valueType, primaryValue, comparisonValue, percentChange, color } = item;

    return (
      <Rows
        style={{
          backgroundColor: publicaColors.white,
          border: `1px solid ${publicaColors.lightGray}`,
          padding: spacing.extraSmall,
          fontSize: fontSizing.extraSmall,
        }}
      >
        <Row smallPadding>
          <Cols style={{ justifyContent: 'space-between', paddingBottom: spacing.extraSmall }}>
            <Col style={{ maxWidth: '208px', paddingRight: spacing.extraLarge }}>
              <Text small bold highlighted>
                {color && (
                  <Span
                    color={color}
                    style={{
                      fontSize: fontSizing.mini,
                      position: 'relative',
                      top: '-2px',
                      paddingRight: spacing.extraSmall,
                    }}
                  >
                    ⬤
                  </Span>
                )}
                {name}
              </Text>
            </Col>
            {percentChange && (
              <Col style={{ paddingTop: '2px' }}>
                <span style={styleCellValue(percentChange, valueTypes.PERCENT_CHANGE)}>
                  {formatCellValue(percentChange, valueTypes.PERCENT_CHANGE, eCurrencyType)}
                </span>
              </Col>
            )}
          </Cols>
          <Cols
            style={{
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              borderTop: `1px solid ${publicaColors.lightGray}`,
              paddingTop: '8px',
            }}
          >
            <Col style={{ paddingRight: spacing.extraLarge }}>
              <Text extraSmall bold tKey={dateRangeTKey}>
                {dateRange}:
              </Text>
            </Col>
            <Col>{formatCellValue(primaryValue, valueType, eCurrencyType)}</Col>
          </Cols>
          {isComparedToEnabled && (
            <Cols style={{ justifyContent: 'space-between', alignItems: 'flex-end' }}>
              <Col style={{ paddingRight: spacing.extraLarge }}>
                <Text extraSmall bold tKey='PreviousPeriod' />
              </Col>
              <Col>{formatCellValue(comparisonValue, valueType, eCurrencyType)}</Col>
            </Cols>
          )}
        </Row>
      </Rows>
    );
  }
  return null;
};

// this will shorten long names ex. Publica Bidder Name => PublicaBidder...
const shortenLabel = (label: string) => {
  if (label.length > 15) {
    return `${label.replace(/ /g, '').slice(0, 12)}...`;
  }
  return label.replace(/ /g, '');
};

interface IRenderChartProps {
  dataLoadState: LoadingState;
  data: IDrillBarChartDataItem[];
  eCurrencyType?: ECurrencyType;
  colorBank: string[];
  handleBarClick: (row: IDrillBarChartDataItem) => void;
  selectedDateRange: string;
  isComparedToEnabled: boolean;
}

const renderChart = ({
  dataLoadState,
  data,
  eCurrencyType,
  selectedDateRange = 'Selected Period',
  colorBank,
  handleBarClick,
  isComparedToEnabled,
}: IRenderChartProps) => {
  const displayedData = dataLoadState === LoadingStateNoData ? [] : data;
  return (
    <BarChart
      data={displayedData}
      layout='vertical'
      barGap={0}
      margin={{ top: 0, right: 16, bottom: 16, left: 48 }}
      style={{ width: '100%', height: '100%' }}
    >
      <CartesianGrid horizontal={false} strokeDasharray='4 1' />
      <XAxis
        type='number'
        tick={{
          fontSize: 12,
          fill: 'black',
          textAnchor: 'end',
        }}
        tickFormatter={convertLargeNumbersToRoundedString}
      />
      <YAxis
        type='category'
        dataKey='name'
        tick={{
          fontSize: 12,
          fill: 'black',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
        tickFormatter={shortenLabel}
        onClick={(props) => {
          const result = displayedData.find((item) => props.value === item.name);
          handleBarClick(result || ({} as IDrillBarChartDataItem));
        }}
      />
      <Tooltip
        content={
          // this bypasses an issue with automated props from recharts
          // @ts-expect-error - Recharts will provide the missing props (active, payload, label) at runtime
          <CustomTooltip
            dateRange={selectedDateRange}
            isComparedToEnabled={isComparedToEnabled}
            eCurrencyType={eCurrencyType}
            dateRangeTKey={dateRangeStringToTKey[selectedDateRange]}
          />
        }
        cursor={{ fill: publicaColors.veryLightGray }}
      />
      <Bar dataKey='primaryValue' barSize={32} radius={[0, 4, 4, 0]}>
        {displayedData.map((item, i) => (
          <Cell
            key={`cell-${i}`}
            fill={(item && (item.color as string)) || colorBank[i % colorBank.length]}
            style={{
              cursor: 'pointer',
            }}
            onClick={() => handleBarClick(item)}
          />
        ))}
      </Bar>
    </BarChart>
  );
};

interface IDrillHorizontalBarChartProps {
  dataLoadState: LoadingState;
  data: IDrillBarChartDataItem[];
  eCurrencyType?: ECurrencyType;
  isComparedToEnabled?: boolean;
  colorBank?: string[];
  title?: string | React.FunctionComponent;
  titleTKey?: TKey;
  titleTProps?: { item?: string; altItem?: string };
  selectedDateRange?: string;
  isChartUpdating?: boolean;
  handleBarClick?: (row: IDrillBarChartDataItem) => void;
}

export const DrillHorizontalBarChart = ({
  dataLoadState,
  data,
  eCurrencyType,
  colorBank = Object.values(defaultColorBank),
  title,
  selectedDateRange,
  isChartUpdating = false,
  isComparedToEnabled = false,
  handleBarClick = () => null,
  titleTKey,
  titleTProps,
}: IDrillHorizontalBarChartProps) => {
  return (
    <Rows>
      {title && (
        <Row style={{ padding: '32px 32px 16px 32px' }}>
          <Text subheader style={{ textAlign: 'center' }} tKey={titleTKey} tProps={titleTProps}>
            {title}
          </Text>
        </Row>
      )}
      {isChartUpdating || dataLoadState === LoadingStateLoading ? (
        <Row style={{ width: '100%' }}>
          <Skeleton
            variant='rect'
            animation='wave'
            width='100%'
            height={320}
            style={{ borderRadius: '4px' }}
          />
        </Row>
      ) : dataLoadState === LoadingStateNoData ? (
        <Row
          style={{
            height: '320px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Text small tKey='NoResultsFound' />
        </Row>
      ) : (
        <Row>
          <ResponsiveContainer width='99%' height={320}>
            {renderChart({
              dataLoadState,
              data,
              eCurrencyType,
              colorBank,
              handleBarClick,
              selectedDateRange: selectedDateRange || '',
              isComparedToEnabled,
            })}
          </ResponsiveContainer>
        </Row>
      )}
    </Rows>
  );
};
